/* ============================================================================
 * shareInbox.js — what another app shared to Milli.
 *
 * The Milli PWA is a Web Share Target (public/milli/manifest.json). Sharing a
 * photo or PDF from Gallery, Drive or Gmail POSTs it to /milli/share; the
 * service worker parks the files and the text in the "ko-share-inbox" cache
 * and redirects to /milli?shared=1. The console calls takeSharedPayload()
 * once on load: it returns the files (as File objects, ready for
 * filesToAttachments) and the text, and empties the inbox so a reload does
 * not attach them twice. Nothing here touches the network.
 * ========================================================================== */

const SHARE_CACHE = 'ko-share-inbox';
const SHARE_META = '/__share/meta';
// A share older than this is stale (the page it was meant for is long gone).
const MAX_AGE_MS = 10 * 60 * 1000;

/** True when this page load came from a share (the redirect adds ?shared=1). */
export const arrivedFromShare = () => {
  try {
    return new URLSearchParams(window.location.search).get('shared') === '1';
  } catch {
    return false;
  }
};

/** Drop ?shared=1 from the address bar without a navigation. */
export const clearShareFlag = () => {
  try {
    const u = new URL(window.location.href);
    if (!u.searchParams.has('shared')) return;
    u.searchParams.delete('shared');
    window.history.replaceState(null, '', u.pathname + (u.search || '') + u.hash);
  } catch {
    /* cosmetic */
  }
};

/**
 * Read and empty the share inbox. Returns { files: File[], text: string }
 * or null when nothing was shared (or the Cache API is unavailable).
 */
export const takeSharedPayload = async () => {
  if (typeof caches === 'undefined') return null;
  try {
    const cache = await caches.open(SHARE_CACHE);
    const metaRes = await cache.match(SHARE_META);
    if (!metaRes) return null;
    const meta = await metaRes.json();
    const files = [];
    for (const f of Array.isArray(meta.files) ? meta.files : []) {
      const res = await cache.match(f.key);
      if (!res) continue;
      const blob = await res.blob();
      files.push(new File([blob], f.name || 'shared', { type: f.type || blob.type || '' }));
    }
    for (const key of await cache.keys()) await cache.delete(key);
    if (Date.now() - Number(meta.at || 0) > MAX_AGE_MS) return null;
    const text = [meta.title, meta.text, meta.url]
      .map((s) => String(s || '').trim())
      .filter((s, i, arr) => s && arr.indexOf(s) === i)
      .join('\n');
    return { files, text };
  } catch {
    return null;
  }
};
